import { create } from 'zustand';
import type { ChatMessage } from '@/types/chat';
import type { ChartData } from '@/types/chart';
import { getAresWallClockMs } from '@/utils/aresDateTime';

interface ChatState {
  messages: ChatMessage[];
  isLoading: boolean;
  loadingStartedAt: number | null;
  reasoning: string;
  currentChart: ChartData | null;
  focusTrip: string | null;
  focusTrips: string[];
  // Track which messages have explicit feedback to prevent duplicate implicit feedback
  explicitlyRatedMessageIds: Set<string>;
  // Track which message is currently being tracked for dwell time
  activeDwellMessageId: string | null;
  activeFilters: Record<string, any>;
  clearGeneration: number;

  addMessage: (message: ChatMessage) => void;
  setMessages: (messages: ChatMessage[]) => void;
  removeMessage: (id: string) => void;
  clearMessages: () => void;
  setLoading: (loading: boolean) => void;
  setReasoning: (text: string) => void;
  setCurrentChart: (chart: ChartData | null) => void;
  setFocusTrip: (alias: string | null) => void;
  setFocusTrips: (aliases: string[]) => void;
  // Mark a message as having explicit feedback
  markMessageAsRated: (messageId: string) => void;
  // Check if a message has explicit feedback
  isMessageRated: (messageId: string) => boolean;
  // Set the message currently being tracked for dwell time
  setActiveDwellMessage: (messageId: string | null) => void;
  // Cancel dwell tracking for a specific message (when user gives explicit feedback)
  cancelDwellTracking: (messageId: string) => void;
  setActiveFilters: (filters: Record<string, any>) => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [],
  isLoading: false,
  loadingStartedAt: null,
  reasoning: '',
  currentChart: null,
  focusTrip: null,
  focusTrips: [],
  explicitlyRatedMessageIds: new Set<string>(),
  activeDwellMessageId: null,
  activeFilters: {},
  clearGeneration: 0,

  addMessage: (message) =>
    set((state) => {
      // Prevent duplicate messages by ID
      if (state.messages.some((m) => m.id === message.id)) {
        return state;
      }
      // Also prevent duplicate AI messages with same content within 1 second
      if (message.sender === 'ai' && state.messages.length > 0) {
        const lastMsg = state.messages[state.messages.length - 1];
        const lastMsgTime = getAresWallClockMs(lastMsg.timestamp);
        const newMsgTime = getAresWallClockMs(message.timestamp);
        if (
          lastMsg.sender === 'ai' &&
          lastMsg.content === message.content &&
          lastMsgTime != null &&
          newMsgTime != null &&
          newMsgTime - lastMsgTime < 1000
        ) {
          return state;
        }
      }
      return { messages: [...state.messages, message] };
    }),

  setMessages: (messages) => set({ messages }),

  removeMessage: (id) =>
    set((state) => ({
      messages: state.messages.filter((m) => m.id !== id),
    })),

  clearMessages: () => set({ 
    messages: [], 
    isLoading: false,
    loadingStartedAt: null,
    reasoning: '', 
    currentChart: null,
    focusTrip: null,
    focusTrips: [],
    explicitlyRatedMessageIds: new Set<string>(),
    activeDwellMessageId: null,
    activeFilters: {},
    clearGeneration: get().clearGeneration + 1,
  }),

  setLoading: (loading) =>
    set({
      isLoading: loading,
      loadingStartedAt: loading ? Date.now() : null,
      ...(loading ? {} : { reasoning: '' }),
    }),

  setReasoning: (text) => set({ reasoning: text }),

  setCurrentChart: (chart) => set({ currentChart: chart }),

  setFocusTrip: (alias) => set({ focusTrip: alias }),

  setFocusTrips: (aliases) => set({ focusTrips: aliases }),

  markMessageAsRated: (messageId: string) =>
    set((state) => ({
      explicitlyRatedMessageIds: new Set([...state.explicitlyRatedMessageIds, messageId]),
    })),

  isMessageRated: (messageId: string) => {
    return get().explicitlyRatedMessageIds.has(messageId);
  },

  setActiveDwellMessage: (messageId: string | null) =>
    set({ activeDwellMessageId: messageId }),

  cancelDwellTracking: (messageId: string) =>
    set((state) => {
      // Only cancel if this is the currently tracked message
      if (state.activeDwellMessageId === messageId) {
        return { activeDwellMessageId: null };
      }
      return state;
    }),

  setActiveFilters: (filters) => set({ activeFilters: filters }),
}));
